import { useCallback, useState } from 'react';
import { useAudioRecorder } from './useAudioRecorder';
import { useVoiceRecognition } from './useVoiceRecognition';
import { storageApi } from '../api/client';

export function useInspectionVoice(lang = 'en-US') {
  const recorder = useAudioRecorder();
  const recognition = useVoiceRecognition(lang);
  const [error, setError] = useState<string | null>(null);

  const startVoice = useCallback(async (): Promise<string | null> => {
    setError(null);
    recognition.clearTranscript();
    try {
      await recorder.startRecording();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start recording';
      setError(msg);
      return msg;
    }
    if (recognition.isAvailable) recognition.startListening();
    return null;
  }, [recorder, recognition]);

  const stopVoice = useCallback(async (): Promise<{ blob: Blob | null; transcript: string }> => {
    recognition.stopListening();
    const blob = await recorder.stopRecording();
    return { blob, transcript: recognition.transcript };
  }, [recorder, recognition]);

  const cancelVoice = useCallback(() => {
    recognition.stopListening();
    recognition.clearTranscript();
    recorder.cancelRecording();
  }, [recorder, recognition]);

  const uploadAndGetBlobName = useCallback(async (blob: Blob): Promise<string> => {
    // Strip codec suffix (e.g. "audio/webm;codecs=opus" → "audio/webm") for backend validation
    const mimeType = blob.type.split(';')[0] || 'audio/webm';
    const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
    const file = new File([blob], `recording.${ext}`, { type: mimeType });
    const { blob_name } = await storageApi.uploadToPresignedUrl(file);
    return blob_name;
  }, []);

  return {
    isRecording: recorder.isRecording,
    durationMs: recorder.durationMs,
    audioLevel: recorder.audioLevel,
    transcript: recognition.transcript,
    interimTranscript: recognition.interimTranscript,
    isTranscriptAvailable: recognition.isAvailable,
    error,
    startVoice,
    stopVoice,
    cancelVoice,
    uploadAndGetBlobName,
  };
}
